// Sticky Header — fixed + spacer + shrink on scroll
class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.getElementById('shopify-section-header');
    this.siteHeader = this.querySelector('.site-header');
    this.isScrolled = false;
    this.ticking = false;
    this.transitionLocked = false;
    this.lockTimer = null;
    this.spacer = null;
    this.expandedHeight = 0;
    this.shrunkHeight = 0;
    this.mobileQuery = window.matchMedia('(max-width: 991.98px)');

    if (!this.header) return;

    this.applyThresholds();
    this.preloadCompactLogos();
    this.createSpacer();
    this.header.classList.add('shopify-section-header-sticky');
    this.measureHeaderHeights();

    this.onScrollHandler = this.onScroll.bind(this);
    this.onResizeHandler = this.onResize.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);

    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    window.addEventListener('resize', this.onResizeHandler, { passive: true });
    if (this.mobileQuery.addEventListener) {
      this.mobileQuery.addEventListener('change', this.onViewportChange);
    } else if (this.mobileQuery.addListener) {
      this.mobileQuery.addListener(this.onViewportChange);
    }

    this.updateScrolledState();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
    window.removeEventListener('resize', this.onResizeHandler);
    if (this.mobileQuery) {
      if (this.mobileQuery.removeEventListener) {
        this.mobileQuery.removeEventListener('change', this.onViewportChange);
      } else if (this.mobileQuery.removeListener) {
        this.mobileQuery.removeListener(this.onViewportChange);
      }
    }
    if (this.lockTimer) clearTimeout(this.lockTimer);
    this.removeSpacer();
  }

  isMobile() {
    return this.mobileQuery && this.mobileQuery.matches;
  }

  applyThresholds() {
    if (this.isMobile()) {
      this.enterThreshold = 100;
      this.exitThreshold = 12;
      this.transitionLockMs = 560;
      this.useTransitionLock = true;
    } else {
      this.enterThreshold = 60;
      this.exitThreshold = 10;
      this.transitionLockMs = 0;
      this.useTransitionLock = false;
      this.transitionLocked = false;
      if (this.lockTimer) {
        clearTimeout(this.lockTimer);
        this.lockTimer = null;
      }
    }
  }

  onViewportChange() {
    this.applyThresholds();
    this.measureHeaderHeights();
  }

  preloadCompactLogos() {
    // if (!this.isMobile()) return;
    this.querySelectorAll('.header-logo__img--compact').forEach((img) => {
      if (typeof img.decode === 'function') {
        img.decode().catch(() => {});
      }
    });
  }

  createSpacer() {
    if (this.spacer) return;

    this.spacer = document.querySelector('[data-header-spacer]');
    if (this.spacer) {
      if (this.header.contains(this.spacer)) {
        this.header.insertAdjacentElement('afterend', this.spacer);
      }
      return;
    }

    this.spacer = document.createElement('div');
    this.spacer.className = 'header-sticky-spacer';
    this.spacer.setAttribute('aria-hidden', 'true');
    this.header.insertAdjacentElement('afterend', this.spacer);
  }

  getMeasureTarget() {
    return this.siteHeader || this;
  }

  removeSpacer() {
    if (this.spacer && this.spacer.parentNode) {
      this.spacer.parentNode.removeChild(this.spacer);
    }
    this.spacer = null;
  }

  measureShrunkHeight() {
    const measureTarget = this.getMeasureTarget();
    if (!measureTarget) return 0;

    const wrapper = document.createElement('div');
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.style.cssText =
      'position:absolute;left:-9999px;top:0;width:100%;visibility:hidden;pointer-events:none;';

    const clone = measureTarget.cloneNode(true);
    clone.classList.add('header--scrolled');
    clone.classList.remove('header--logo-settled');
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    const height = clone.offsetHeight;
    document.body.removeChild(wrapper);
    return height;
  }

  setSpacerHeight(height) {
    if (!height || !this.spacer) return;

    const currentHeight = parseFloat(window.getComputedStyle(this.spacer).height) || 0;
    if (Math.abs(currentHeight - height) < 2) return;

    this.spacer.style.height = height + 'px';
    document.documentElement.style.setProperty('--header-spacer-height', height + 'px');
  }

  measureHeaderHeights() {
    if (!this.header) return;

    const measureTarget = this.getMeasureTarget();
    this.expandedHeight = measureTarget.offsetHeight;
    this.shrunkHeight = this.measureShrunkHeight() || this.expandedHeight;

    this.setSpacerHeight(this.expandedHeight);
    this.header.style.setProperty('--header-height', this.expandedHeight + 'px');
    this.applyHeaderOffset();
  }

  applyHeaderOffset() {
    const height = this.isScrolled ? this.shrunkHeight : this.expandedHeight;
    if (!height) return;
    document.documentElement.style.setProperty('--header-offset', Math.round(height - 1) + 'px');
  }

  onResize() {
    if (this.transitionLocked) return;
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.applyThresholds();
      this.measureHeaderHeights();
    }, 150);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;

    requestAnimationFrame(() => {
      this.updateScrolledState();
      this.ticking = false;
    });
  }

  getScrollY() {
    return window.scrollY || document.documentElement.scrollTop || 0;
  }

  lockTransition() {
    if (!this.useTransitionLock) return;

    this.transitionLocked = true;
    if (this.lockTimer) clearTimeout(this.lockTimer);
    this.lockTimer = setTimeout(() => {
      this.transitionLocked = false;
      this.lockTimer = null;
    }, this.transitionLockMs);
  }

  updateScrolledState() {
    if (this.transitionLocked) return;

    const scrollY = this.getScrollY();
    let shouldShrink = this.isScrolled;

    if (!this.isScrolled && scrollY > this.enterThreshold) {
      shouldShrink = true;
    } else if (this.isScrolled && scrollY <= this.exitThreshold) {
      shouldShrink = false;
    }

    if (shouldShrink === this.isScrolled) return;

    this.isScrolled = shouldShrink;
    this.header.classList.toggle('header--scrolled', shouldShrink);
    this.header.classList.remove('header--logo-settled');

    if (this.siteHeader) {
      this.siteHeader.classList.toggle('header--scrolled', shouldShrink);
      this.siteHeader.classList.remove('header--logo-settled');
    }

    this.applyHeaderOffset();
    this.lockTransition();

    if (shouldShrink) {
      this.closeMenuDropdowns();
    }
  }

  closeMenuDropdowns() {
    const openDropdowns = this.header.querySelectorAll('.dropdown.open');
    openDropdowns.forEach((ele) => {
      ele.classList.remove('open');
    });
  }
}

customElements.define('sticky-header', StickyHeader);
